import React, { useState, useRef } from 'react';
import ApplicationFileUpload from './ApplicationFileUpload';
import { useTranslation } from '../../../TranslationContext';

type FileState = 'empty' | 'uploading' | 'success' | 'error';

interface ApplicationFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  companyId: string;
  onSubmit: (data: FormData) => Promise<void>;
}

interface SanitizedInput {
  name: string;
  redirectUri: string;
  logOutRedirectUri: string;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({ onSuccess, onError, companyId, onSubmit }) => {
  const { t } = useTranslation();
  const certificateInputRef = useRef<HTMLInputElement>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    redirectUri: '',
    logOutRedirectUri: '',
    companyId,
  });
  
  // Certificate file state
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [certificateState, setCertificateState] = useState<FileState>('empty');
  const [certificateError, setCertificateError] = useState<string | null>(null);
  
  // File upload states for roles
  const [rolesFile, setRolesFile] = useState<File | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [fileState, setFileState] = useState<FileState>('empty');
  const [fileError, setFileError] = useState<string | null>(null);
  
  // Submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * Validates and sanitizes form inputs
   */
  const validateAndSanitize = (): SanitizedInput | null => {
    const sanitized = {
      name: formData.name.replace(/[<>"']/g, '').trim(),
      redirectUri: formData.redirectUri.replace(/[<>"']/g, '').trim(),
      logOutRedirectUri: formData.logOutRedirectUri.replace(/[<>"']/g, '').trim(),
    };

    if (!sanitized.name || !sanitized.redirectUri) {
      setFormError(t('error_required_fields'));
      return null;
    }

    if (!certificateFile) {
      setFormError(t('error_certificate_required'));
      return null;
    }

    try {
      new URL(sanitized.redirectUri);
      if (!sanitized.redirectUri.startsWith('https://')) {
        throw new Error(t('error_https_required'));
      }
    } catch {
      setFormError(t('error_invalid_url'));
      return null;
    }

    // Validate postLogoutRedirectUri if provided
    if (sanitized.logOutRedirectUri) {
      try {
        new URL(sanitized.logOutRedirectUri);
        if (!sanitized.logOutRedirectUri.startsWith('https://')) {
          throw new Error(t('error_https_required'));
        }
      } catch {
        setFormError(t('error_invalid_logout_url'));
        return null;
      }
    }

    return sanitized;
  };

  /**
   * Handles form submission
   */

  const handleSubmit = async () => {
    const sanitized = validateAndSanitize();
    if (!sanitized) return;
  
    setIsSubmitting(true);
    setFormError(null);
  
    try {
      const formDataToSend = new FormData();
      // Utiliser les noms exacts attendus par l'API
      formDataToSend.append('Name', sanitized.name);
      formDataToSend.append('DisplayName', sanitized.name); // Même que Name si pas spécifié ailleurs
      formDataToSend.append('RedirectUri', sanitized.redirectUri);
      formDataToSend.append('LogOutRedirectUri', sanitized.logOutRedirectUri);
      formDataToSend.append('CompanyId', companyId);
      
      // ClientId et ClientSecret peuvent être vides pour une nouvelle application
      formDataToSend.append('ClientId', '');
      formDataToSend.append('ClientSecret', '');
  
      // Ajouter le certificat avec le nom correct
      if (certificateFile) {
        formDataToSend.append('Certificate', certificateFile);
      }
      
      // Si vous avez un fichier de rôles
      if (rolesFile) {
        formDataToSend.append('File', rolesFile);
        formDataToSend.append('rolesCount', roles.length.toString());
      }
  
      console.log('Données du formulaire envoyées:', formDataToSend);
      for (const pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }
      
      await onSubmit(formDataToSend);
      resetForm();
      onSuccess?.();
    } catch (err) {
      const errorMessage = t('error_create_application');
      setFormError(errorMessage);
      onError?.(errorMessage);
      console.error(t('error_submission_log'), err);
    } finally {
      setIsSubmitting(false);
    }
  };
  

  /**
   * Resets the form to initial state
   */
  const resetForm = () => {
    setFormData({ name: '', redirectUri: '', logOutRedirectUri: '', companyId });
    setCertificateFile(null);
    setCertificateState('empty');
    setCertificateError(null);
    setRolesFile(null);
    setRoles([]);
    setFileState('empty');
    setFileError(null);
  };

  /**
   * Handles the certificate file upload
   */
  const handleCertificateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Valider que l'extension est .crt
      if (!file.name.toLowerCase().endsWith('.crt')) {
        setCertificateState('error');
        setCertificateError(t('error_invalid_certificate_format'));
        setCertificateFile(null);
        return;
      }
  
      // Validation de la taille du fichier
      if (file.size > 1024 * 1024) { // 1MB max
        setCertificateState('error');
        setCertificateError(t('error_certificate_too_large'));
        setCertificateFile(null);
        return;
      }
  
      setCertificateFile(file);
      setCertificateState('success');
      setCertificateError(null);
    }
  };

  /**
   * Handles removing the certificate file
   */
  const handleRemoveCertificate = () => {
    setCertificateFile(null);
    setCertificateState('empty');
    setCertificateError(null);
    if (certificateInputRef.current) {
      certificateInputRef.current.value = '';
    }
  };

  /**
   * Opens file selection dialog for certificate
   */
  const triggerCertificateUpload = () => {
    if (certificateInputRef.current) {
      certificateInputRef.current.click();
    }
  };

  /**
   * Handles roles file upload changes
   */
  const handleFileChange = (file: File | null, roles: string[] | null, error?: string) => {
    if (error) {
      setFileState('error');
      setFileError(t(error));
      setRolesFile(null);
      setRoles([]);
      return;
    }

    if (file && roles) {
      setFileState('success');
      setFileError(null);
      setRolesFile(file);
      setRoles(roles);
    } else {
      setFileState('empty');
      setFileError(null);
      setRolesFile(null);
      setRoles([]);
    }
  };

  /**
   * Removes the uploaded roles file
   */
  const handleRemoveFile = () => {
    setFileState('empty');
    setRolesFile(null);
    setRoles([]);
    setFileError(null);
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto px-4 py-5 sm:p-6">
      <div className="space-y-4">
        {/* Global error display */}
        {formError && (
          <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {formError}
          </div>
        )}

        {/* Application Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('application_name')} *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-900"
            required
            placeholder={t('application_name_placeholder')}
            maxLength={100}
          />
        </div>

        {/* Redirect URI */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('redirect_uri')} *
          </label>
          <input
            type="url"
            value={formData.redirectUri}
            onChange={(e) => setFormData({ ...formData, redirectUri: e.target.value })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-900"
            placeholder={t('redirect_uri_placeholder')}
            required
            pattern="https://.*"
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('redirect_uri_hint')}
          </p>
        </div>

        {/* Post Logout Redirect URI */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('post_logout_redirect_uri')}
          </label>
          <input
            type="url"
            value={formData.logOutRedirectUri}
            onChange={(e) => setFormData({ ...formData, logOutRedirectUri: e.target.value })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-900"
            placeholder={t('post_logout_redirect_uri_placeholder')}
            pattern="https://.*"
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('post_logout_redirect_uri_hint')}
          </p>
        </div>

        {/* Certificate Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('certificate')} *
          </label>
          
          <input
            type="file"
            ref={certificateInputRef}
            onChange={handleCertificateChange}
            className="hidden"
            accept=".crt"
          />
       
          {certificateState === 'empty' ? (
            <div className="mt-1">
              <button
                type="button"
                onClick={triggerCertificateUpload}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('upload_certificate')}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                {t('certificate_upload_hint')}
              </p>
            </div>
          ) : (
            <div className="mt-2">
              {/* Certificate upload success */}
              {certificateState === 'success' && certificateFile && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-green-800">
                        {certificateFile.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCertificate}
                      className="text-sm text-red-600 hover:text-red-500"
                      aria-label={t('remove_certificate')}
                    >
                      {t('remove')}
                    </button>
                  </div>
                </div>
              )}

              {/* Certificate error display */}
              {certificateState === 'error' && certificateError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-red-800">
                        {certificateError}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCertificate}
                      className="text-sm text-red-600 hover:text-red-500"
                      aria-label={t('try_again')}
                    >
                      {t('try_again')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Roles Upload Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('application_roles')}
          </label>
          
          {fileState === 'empty' ? (
            <>
              <div className="mt-1">
                <ApplicationFileUpload 
                  onFileChange={handleFileChange}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {t('roles_upload_hint')}
              </p>
            </>
          ) : (
            <div className="mt-2 space-y-3">
              {/* File upload status */}
              {fileState === 'success' && rolesFile && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-green-800">
                        {t('file_upload_success' as any, { fileName: rolesFile.name, rolesCount: roles.length })}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="text-sm text-red-600 hover:text-red-500"
                      aria-label={t('remove_file')}
                    >
                      {t('remove')}
                    </button>
                  </div>
                </div>
              )}

              {/* File error display */}
              {fileState === 'error' && fileError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm font-medium text-red-800">
                        {fileError}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="text-sm text-red-600 hover:text-red-500"
                      aria-label={t('try_again')}
                    >
                      {t('try_again')}
                    </button>
                  </div>
                </div>
              )}

              {/* Roles preview */}
              {roles.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('roles_preview')}
                  </p>
                  <div className="max-h-40 overflow-y-auto">
                    <div className="flex flex-wrap gap-1">
                      {roles.map((role, index) => (
                        <span 
                          key={`role-${index}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-gray-800"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Submit button - fixed to bottom on mobile */}
        <div className="pt-6 sticky bottom-0 bg-white">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('creating_application')}
                </span>
              ) : (
                t('create_application')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
